<template>
  <Transition name="fade" mode="out-in">
    <router-view :key="router.currentRoute.name" />
  </Transition>
  <!-- b-share: a share link's ?code= is kept for checkout (browser only). -->
  <PendingCodeNote />
</template>


<script setup>
import {computed, onMounted, watch} from "vue";
import {useRoute} from "vue-router";
import {useHead} from "@vueuse/head";
import {storeToRefs} from "pinia";
import router from "@/router/index.js";
import {useColorStore} from "@/classes/useColorStore.js";
import {usePageStore} from "@/stores/pageStore.js";
import {themeAttributes, themeTokens} from "@/themes/tokens.js";
import {loadThemeFonts} from "@/themes/fonts.js";
import {installMotionObserver} from "@/motion/motion.js";
import {useFootnotes} from "@/stores/footnotes.js";
import PendingCodeNote from "@/shop/PendingCodeNote.vue";
import {installNavTrail} from "@/composables/useNavTrail.js";

const route = useRoute();

// Footnotes are per page: start each route with an empty list. (Client only:
// the router is one shared module on the server, and every server render
// gets a fresh store anyway.)
const footnotes = useFootnotes();
if (!import.meta.env.SSR) router.beforeEach(() => { footnotes.clear(); });

// The theme on <html> (WEB_V2_BUILD.md §4): its name and shape attributes for
// the scoped theme CSS, and the --t-* tokens, so teleported parts (the big
// menu, sheets) see them too. The server writes them into the page (first
// paint is final); on the client they're kept in step directly, replacing the
// old values (the studio preview switches themes live, and nothing from the
// last theme may linger). Legacy --theme-* tokens stay on the page root.
const {theme} = storeToRefs(useColorStore());
const pageStore = usePageStore();
const htmlVars = computed(() => themeTokens(theme.value, pageStore.pageData?.brandColors || {}, pageStore.pageData?.look));
const htmlAttrs = computed(() => themeAttributes(theme.value, pageStore.pageData?.look, pageStore.pageData?.brandColors || {}));
if (import.meta.env.SSR) {
  const style = Object.entries(htmlVars.value).map(([k, v]) => `${k}:${v}`).join(";");
  useHead({htmlAttrs: {...htmlAttrs.value, style}});
}
let shownVars = {};
function applyToHtml() {
  const el = document.documentElement;
  for (const k of Object.keys(shownVars)) if (!(k in htmlVars.value)) el.style.removeProperty(k);
  for (const [k, v] of Object.entries(htmlVars.value)) el.style.setProperty(k, String(v));
  shownVars = {...htmlVars.value};
  for (const k of OPTIONAL_ATTRS) if (!(k in htmlAttrs.value)) el.removeAttribute(k);
  for (const [k, v] of Object.entries(htmlAttrs.value)) el.setAttribute(k, v);
}
// Attributes only some looks set; they go away when the look no longer sets them.
const OPTIONAL_ATTRS = ["data-photos", "data-hero", "data-palette"];
onMounted(() => {
  installNavTrail(router, pageStore);
  applyToHtml();
  watch([htmlVars, htmlAttrs], applyToHtml);
  loadThemeFonts(theme.value, pageStore.pageData?.look);
  installMotionObserver();
});
watch([theme, () => pageStore.pageData?.look?.fonts], ([t]) => loadThemeFonts(t, pageStore.pageData?.look));
</script>
<style scoped>
/* Page Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(20px); /* Slight upwards movement */
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-20px); /* Slight fade-out movement */
}

/* Slide transition */
.slide-enter-active, .slide-leave-active {
  transition: transform 1.5s ease-in-out, opacity 1.5s ease-in-out;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}


</style>